import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import PlatformOwnerAppShell from '../components/PlatformOwnerAppShell';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Save, ExternalLink, Trash2, Globe } from 'lucide-react';

const PLATFORMS = [
  { id: 'facebook', label: 'Facebook', placeholder: 'https://facebook.com/hirehubaustralia', color: '#1877F2' },
  { id: 'instagram', label: 'Instagram', placeholder: 'https://instagram.com/hirehubaustralia', color: '#E4405F' },
  { id: 'tiktok', label: 'TikTok', placeholder: 'https://tiktok.com/@hirehubaustralia', color: '#000000' },
  { id: 'twitter', label: 'X / Twitter', placeholder: 'https://x.com/hirehubaustralia', color: '#000000' },
  { id: 'youtube', label: 'YouTube', placeholder: 'https://youtube.com/@hirehubaustralia', color: '#FF0000' },
  { id: 'linkedin', label: 'LinkedIn', placeholder: 'https://linkedin.com/company/hirehubaustralia', color: '#0A66C2' },
];

export default function SocialLinksSettings() {
  const { api } = useAuth();
  const [links, setLinks] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/platform-owner/social-links');
        setLinks(res.data.links || {});
      } catch { /* first load — empty */ }
      setLoading(false);
    })();
  }, [api]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put('/platform-owner/social-links', { links });
      setLinks(res.data.links);
      toast.success('Social links saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  const activeCount = Object.values(links).filter(Boolean).length;

  return (
    <PlatformOwnerAppShell activeId="social-links" title="Social Links">
      <div className="max-w-2xl mx-auto" data-testid="social-links-page">
        <Card data-testid="social-links-settings">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <Globe className="w-5 h-5 text-[#004B8D]" />
                Social Links
              </CardTitle>
              <Badge variant="secondary">{activeCount} active</Badge>
            </div>
            <p className="text-sm text-slate-500 mt-1">Manage links shown in the "Follow us" section on the landing page. Only platforms with a valid URL will appear publicly.</p>
          </CardHeader>
          <CardContent className="space-y-4">
            {loading ? (
              <div className="animate-pulse space-y-4">
                {[1,2,3].map(i => <div key={i} className="h-12 bg-slate-100 rounded-lg" />)}
              </div>
            ) : (
              <>
                {PLATFORMS.map(p => (
                  <div key={p.id} className="space-y-1.5">
                    <Label className="text-sm font-medium flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full" style={{ background: links[p.id] ? p.color : '#cbd5e1' }} />
                      {p.label}
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        data-testid={`social-input-${p.id}`}
                        value={links[p.id] || ''}
                        onChange={e => setLinks(prev => ({ ...prev, [p.id]: e.target.value }))}
                        placeholder={p.placeholder}
                        className="flex-1 text-sm"
                      />
                      {links[p.id] && (
                        <>
                          <Button variant="ghost" size="icon" className="shrink-0" onClick={() => window.open(links[p.id], '_blank')} data-testid={`social-open-${p.id}`}>
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="shrink-0 text-red-500 hover:text-red-600 hover:bg-red-50" onClick={() => setLinks(prev => ({ ...prev, [p.id]: '' }))} data-testid={`social-clear-${p.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}

                <Button onClick={handleSave} disabled={saving} className="w-full sm:w-auto bg-[#004B8D] hover:bg-[#003a6d] mt-4" data-testid="save-social-links-btn">
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Social Links'}
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </PlatformOwnerAppShell>
  );
}
